import type {
  CalendarMonth,
  HealthResponse,
  HomeSummary,
  ImportantDatePayload,
  ImportantDateWithOccurrence,
  Person,
  PersonPayload,
  TargetType,
} from './types';
import { localApi, localRuntime } from './localClient';
import type { AccountSession } from '../auth/accountApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';
const ACCOUNT_API_URL = (import.meta.env.VITE_ACCOUNT_API_URL as string | undefined)?.replace(/\/$/, '');
let activeAccountSession: AccountSession | null = null;

export const configureAccountDataSession = (session: AccountSession | null) => {
  activeAccountSession = session?.entitlement.capabilities.includes('sync') ? session : null;
};

type RequestOptions = RequestInit & {
  query?: Record<string, string | number | boolean | undefined>;
};

type ApiErrorPayload = {
  error?: {
    code?: string;
    message?: string;
  };
};

const buildUrl = (path: string, query?: RequestOptions['query']) => {
  const url = new URL(`${API_BASE_URL}${path}`, window.location.origin);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url;
};

const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const { query, headers, ...rest } = options;
  const response = await fetch(buildUrl(path, query), {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...headers,
    },
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as ApiErrorPayload | null;
    throw new Error(payload?.error?.message ?? '요청에 실패했습니다.');
  }

  return (await response.json()) as T;
};

const jsonOptions = (method: 'POST' | 'PATCH', payload: unknown): RequestOptions => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

const remoteApi = {
  getHealth: () => request<HealthResponse>('/health'),

  getPeople: (query?: { q?: string; targetType?: TargetType }) => request<Person[]>('/people', { query }),
  getPersonById: (personId: string) => request<Person>(`/people/${personId}`),
  createPerson: (payload: PersonPayload) => request<Person>('/people', jsonOptions('POST', payload)),
  updatePerson: (personId: string, payload: Partial<PersonPayload>) =>
    request<Person>(`/people/${personId}`, jsonOptions('PATCH', payload)),
  deletePerson: (personId: string) => request<{ ok: boolean }>(`/people/${personId}`, { method: 'DELETE' }),

  getPersonDates: (personId: string, year = new Date().getFullYear()) =>
    request<ImportantDateWithOccurrence[]>(`/people/${personId}/dates`, { query: { year } }),
  createPersonDate: (personId: string, payload: ImportantDatePayload) =>
    request<ImportantDateWithOccurrence>(`/people/${personId}/dates`, jsonOptions('POST', payload)),
  getImportantDateById: (id: string, year = new Date().getFullYear()) =>
    request<ImportantDateWithOccurrence>(`/important-dates/${id}`, { query: { year } }),
  updateImportantDate: (id: string, payload: Partial<ImportantDatePayload>) =>
    request<ImportantDateWithOccurrence>(`/important-dates/${id}`, jsonOptions('PATCH', payload)),
  deleteImportantDate: (id: string) => request<{ ok: boolean }>(`/important-dates/${id}`, { method: 'DELETE' }),

  getHome: (days = 60, year = new Date().getFullYear()) => request<HomeSummary>('/home', { query: { days, year } }),
  getCalendarMonth: (year: number, month: number) => request<CalendarMonth>('/calendar', { query: { year, month } }),
};

const accountRequest = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  if (!ACCOUNT_API_URL || !activeAccountSession) throw new Error('계정 동기화 연결이 필요합니다. 다시 로그인해 주세요.');
  const { query, headers, ...rest } = options;
  const url = new URL(`${ACCOUNT_API_URL}/v1${path}`);
  if (query) Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  });
  const response = await fetch(url, {
    ...rest,
    headers: { Accept: 'application/json', Authorization: `Bearer ${activeAccountSession.token}`, ...headers },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as ApiErrorPayload | null;
    if (response.status === 402) throw new Error('동기화와 광고 제거는 1회 구매 후 사용할 수 있습니다.');
    throw new Error(payload?.error?.message ?? '계정 데이터를 불러오지 못했습니다.');
  }
  return response.status === 204 ? undefined as T : await response.json() as T;
};

const accountApi: typeof remoteApi = {
  getHealth: async () => {
    await accountRequest('/session');
    return { ok: true, db: { ready: true } };
  },
  getPeople: (query) => accountRequest<Person[]>('/people', { query }),
  getPersonById: (id) => accountRequest<Person>(`/people/${id}`),
  createPerson: (payload) => accountRequest<Person>('/people', jsonOptions('POST', payload)),
  updatePerson: (id, payload) => accountRequest<Person>(`/people/${id}`, jsonOptions('PATCH', payload)),
  deletePerson: (id) => accountRequest<{ ok: boolean }>(`/people/${id}`, { method: 'DELETE' }),
  getPersonDates: (id, year = new Date().getFullYear()) => accountRequest<ImportantDateWithOccurrence[]>(`/people/${id}/dates`, { query: { year } }),
  createPersonDate: (id, payload) => accountRequest<ImportantDateWithOccurrence>(`/people/${id}/dates`, jsonOptions('POST', payload)),
  getImportantDateById: (id, year = new Date().getFullYear()) => accountRequest<ImportantDateWithOccurrence>(`/important-dates/${id}`, { query: { year } }),
  updateImportantDate: (id, payload) => accountRequest<ImportantDateWithOccurrence>(`/important-dates/${id}`, jsonOptions('PATCH', payload)),
  deleteImportantDate: (id) => accountRequest<{ ok: boolean }>(`/important-dates/${id}`, { method: 'DELETE' }),
  getHome: (days = 60, year = new Date().getFullYear()) => accountRequest<HomeSummary>('/home', { query: { days, year } }),
  getCalendarMonth: (year, month) => accountRequest<CalendarMonth>('/calendar', { query: { year, month } }),
};

export const importLocalDataToAccount = async (payload: { people: Person[]; importantDates: import('./types').ImportantDate[] }, idempotencyKey: string) =>
  accountRequest<{ ok: boolean; imported: { people: number; importantDates: number } }>('/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(payload),
  });

export const api: typeof remoteApi = new Proxy({} as typeof remoteApi, {
  get: (_target, property: keyof typeof remoteApi) => {
    const source = activeAccountSession ? accountApi : (localRuntime.enabled ? localApi : remoteApi);
    return source[property];
  },
});
export { localRuntime };
